import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  ModalCloseButton,
  Select as ChakraSelect,
  Stack,
  useToast,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { AccountType, accountTypes, AccountTypeValue } from 'api/accountType';
import { ButtonSpinner } from 'components/LoadingSpinner';
import AlertModal from 'components/modals/AlertModal';
import { useCountryName } from 'helpers/localization';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useStoreAccountMutation from 'hooks/mutations/useStoreAccountMutation';
import usePortalsQuery from 'hooks/usePortalsQuery';
import { useEffect, useRef } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

export type AddAccountFormValues = {
  portal_id: number;
  type: AccountTypeValue;
  status: 'registered' | 'not_registered';
  username: string;
  password: string;
  create_task: boolean;
};

type AddAccountModalProps = {
  workshopId: number;
  workshopCountry: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Partial<AddAccountFormValues>;
  // when provided, called with the newly created account's id instead of the default
  // navigate-to-account-page behavior, so the caller can chain its own follow-up flow
  onSuccess?: (accountId: number) => void;
};

// Mirrors the backend's enforcement in AccountController::store() (which returns 400 for any other
// type) -- kept here only to drive the UI; the backend remains the actual authority.
const taskEligibleTypes: AccountTypeValue[] = [AccountType.WP, AccountType.WP_SUB_ACCOUNT];

const DEFAULT_VALUES: Partial<AddAccountFormValues> = {
  portal_id: undefined,
  type: AccountType.WP,
  status: 'registered',
  username: '',
  password: '',
  create_task: false,
};

const AddAccountModal = ({ workshopId, workshopCountry, isOpen, onClose, initialValues, onSuccess }: AddAccountModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast(defaultToastOptions);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const portalsQuery = usePortalsQuery();
  const storeAccountMutation = useStoreAccountMutation(workshopId);
  const countryName = useCountryName(workshopCountry);

  const schema = yup
    .object({
      portal_id: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' || originalValue == null ? undefined : value))
        .typeError(t('workshop:add_account.form.portal_required', { defaultValue: 'Portal is required' }))
        .required(t('workshop:add_account.form.portal_required', { defaultValue: 'Portal is required' })),
      type: yup
        .string()
        .oneOf([...accountTypes])
        .transform((value) => (value === '' ? undefined : value))
        .required(t('workshop:add_account.form.type_required', { defaultValue: 'Type is required' })),
      status: yup.string().oneOf(['registered', 'not_registered']).required(),
      username: yup.string().when(['status', 'create_task', 'type'], {
        is: (status: string, create_task: boolean, type: string) =>
          status === 'not_registered' && create_task === true && taskEligibleTypes.includes(type as AccountTypeValue),
        then: (fieldSchema) => fieldSchema.notRequired(),
        otherwise: (fieldSchema) => fieldSchema.required(t('workshop:add_account.form.username_required', { defaultValue: 'Username is required' })),
      }),
      password: yup.string().when(['status', 'create_task', 'type'], {
        is: (status: string, create_task: boolean, type: string) =>
          status === 'not_registered' && create_task === true && taskEligibleTypes.includes(type as AccountTypeValue),
        then: (fieldSchema) => fieldSchema.notRequired(),
        otherwise: (fieldSchema) => fieldSchema.required(t('workshop:add_account.form.password_required', { defaultValue: 'Password is required' })),
      }),
      create_task: yup.bool().required(),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    control,
    watch,
    setValue,
    reset,
  } = useForm<AddAccountFormValues>({
    resolver: yupResolver(schema) as Resolver<AddAccountFormValues>,
    defaultValues: { ...DEFAULT_VALUES, ...initialValues },
    mode: 'onChange',
  });

  const status = watch('status');
  const type = watch('type');
  const createTask = watch('create_task');

  const isTaskEligibleType = taskEligibleTypes.includes(type as AccountTypeValue);
  const showTriggerRegistration = status === 'not_registered' && isTaskEligibleType;

  useEffect(() => {
    if (!showTriggerRegistration && createTask) {
      setValue('create_task', false);
    }
  }, [showTriggerRegistration, createTask, setValue]);

  // Only WP/WP_SUB_ACCOUNT accounts can ever be Not Registered (the backend always
  // creates other types as Registered) -- force the status back so the UI can't imply otherwise.
  useEffect(() => {
    if (!isTaskEligibleType && status !== 'registered') {
      setValue('status', 'registered');
    }
  }, [isTaskEligibleType, status, setValue]);

  useEffect(() => {
    if (storeAccountMutation.isError) {
      toast({ title: storeAccountMutation.error.message ?? t('errors:general'), status: 'error' });
    }
  }, [storeAccountMutation.isError, storeAccountMutation.error, t, toast]);

  // re-apply the (possibly caller-supplied) defaults every time the modal opens, since react-hook-form
  // only reads `defaultValues` once at mount and this instance can be reopened with different values
  useEffect(() => {
    if (isOpen) {
      reset({ ...DEFAULT_VALUES, ...initialValues });
    }
  }, [isOpen, initialValues, reset]);

  const handleClose = () => {
    reset({ ...DEFAULT_VALUES, ...initialValues });
    onClose();
  };

  const doSubmit = (data: AddAccountFormValues) => {
    storeAccountMutation.mutate(
      {
        workshop_id: String(workshopId),
        portal_id: String(data.portal_id),
        type: data.type,
        username: data.username,
        password: data.password,
        create_task: data.create_task,
      },
      {
        onSuccess: (response) => {
          handleClose();
          if (!response.data?.id) {
            return;
          }
          if (onSuccess) {
            onSuccess(Number(response.data.id));
          } else {
            navigate(`/app/op/account/${response.data.id}`);
          }
        },
      },
    );
  };

  const requiresCountryConfirmation = !!workshopCountry && workshopCountry.toUpperCase() !== 'DE';

  const onSubmit = (data: AddAccountFormValues) => {
    if (requiresCountryConfirmation) {
      NiceModal.show(AlertModal, {
        onSubmit: () => {
          doSubmit(data);
          return Promise.resolve();
        },
        children: t('workshop:add_account.confirm_non_de_country.text', { country: countryName }),
        content: {
          header: t('workshop:add_account.confirm_non_de_country.header'),
          footer: {
            buttons: {
              cancelCaption: t('common:cancel'),
              actionCaption: t('workshop:add_account.form.submit'),
            },
          },
        },
      });
      return;
    }

    doSubmit(data);
  };

  return (
    <AlertDialog isOpen={isOpen} onClose={handleClose} leastDestructiveRef={cancelRef} motionPreset="slideInBottom" closeOnOverlayClick={false}>
      <AlertDialogOverlay sx={{ backdropFilter: 'blur(2rem)' }}>
        <AlertDialogContent as="form" onSubmit={handleSubmit(onSubmit)} maxW="28rem" data-test-id="add-account-modal">
          <AlertDialogHeader>{t('workshop:add_account.modal.header', { defaultValue: 'Add Account' })}</AlertDialogHeader>
          <ModalCloseButton data-test-id="add-account-modal-close" />
          <AlertDialogBody>
            <Stack spacing={4}>
              <FormControl isInvalid={!!errors.portal_id}>
                <FormLabel>{t('workshop:add_account.form.portal_label', { defaultValue: 'Portal' })}</FormLabel>
                <Controller
                  name="portal_id"
                  control={control}
                  render={({ field }) => (
                    <ChakraSelect
                      placeholder={t('workshop:add_account.form.portal_placeholder', { defaultValue: 'Select portal' })}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === '' ? undefined : Number(val));
                      }}
                    >
                      {portalsQuery.data?.map((portal) => (
                        <option key={portal.id} value={portal.id}>
                          {portal.name}
                        </option>
                      ))}
                    </ChakraSelect>
                  )}
                />
                <FormErrorMessage>{errors.portal_id?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.type}>
                <FormLabel>{t('workshop:add_account.form.type_label', { defaultValue: 'Type' })}</FormLabel>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <ChakraSelect
                      placeholder={t('workshop:add_account.form.type_placeholder', { defaultValue: 'Select account type' })}
                      value={(field.value as AccountTypeValue | '') ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === '' ? undefined : (val as AccountTypeValue));
                      }}
                    >
                      {accountTypes.map((type) => (
                        <option key={type} value={type}>
                          {t(`account:type.${type}.label`)}
                        </option>
                      ))}
                    </ChakraSelect>
                  )}
                />
                <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
              </FormControl>

              {isTaskEligibleType && (
                <FormControl isInvalid={!!errors.status}>
                  <FormLabel>{t('account:data_table.status.label')}</FormLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <ChakraSelect value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                        <option value="registered">{t('account:status.registered.label')}</option>
                        <option value="not_registered">{t('account:status.not_registered.label')}</option>
                      </ChakraSelect>
                    )}
                  />
                  <FormErrorMessage>{errors.status?.message}</FormErrorMessage>
                </FormControl>
              )}

              {showTriggerRegistration && (
                <FormControl isInvalid={!!errors.create_task}>
                  <Controller
                    name="create_task"
                    control={control}
                    render={({ field }) => (
                      <Checkbox isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)}>
                        {t('workshop:add_account.form.trigger_registration_label')}
                      </Checkbox>
                    )}
                  />
                  <FormErrorMessage>{errors.create_task?.message}</FormErrorMessage>
                </FormControl>
              )}

              <FormControl isInvalid={!!errors.username}>
                <FormLabel>{t('workshop:add_account.form.username_label', { defaultValue: 'Username' })}</FormLabel>
                <Input {...register('username')} type="text" autoComplete="off" />
                <FormErrorMessage>{errors.username?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel>{t('workshop:add_account.form.password_label', { defaultValue: 'Password' })}</FormLabel>
                <Input {...register('password')} type="password" autoComplete="new-password" />
                <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Grid templateColumns="repeat(2, 1fr)" gap={3}>
              <Button variant="outline" ref={cancelRef} onClick={handleClose} disabled={storeAccountMutation.isPending}>
                {t('common:cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={portalsQuery.isLoading || storeAccountMutation.isPending || !isValid}
                data-test-id="add-account-submit"
              >
                {t('workshop:add_account.form.submit', { defaultValue: 'Add Account' })} {storeAccountMutation.isPending ? <ButtonSpinner /> : null}
              </Button>
            </Grid>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default AddAccountModal;
